import { Button } from "@dmca/frontend/components/button";
import { Controller, Input, PhoneInput } from "@dmca/frontend/components/input";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  rootRouteId,
  useRouter,
} from "@tanstack/react-router";
import {
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from "@dmca/utils/libphonenumber";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  boolean,
  check,
  minLength,
  object,
  pipe,
  string,
  type InferOutput,
} from "valibot";
import { authClient, useCountdownTimer, useUser } from "@dmca/frontend/hooks";
import { useRef, useState, type FormEvent } from "react";
import { userPinApi } from "../../../../../user-pin-api";
import { runPinWriteOnce } from "../../../../../pin-write";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/settings/user/profile"
)({
  component: RouteComponent,
});

const FormValues = object({
  firstName: pipe(string(), minLength(1, "First name is required.")),
  lastName: pipe(string(), minLength(1, "Last name is required.")),
  phoneNumber: pipe(
    object({
      value: string(),
      isVerified: boolean(),
    }),
    check(
      (value) => !value.value || isValidPhoneNumber(value.value),
      "Invalid phone number."
    ),
    check(
      (value) => !value.value || value.isVerified,
      "Phone number must be verified."
    )
  ),
});
type FormValues = InferOutput<typeof FormValues>;

function RouteComponent() {
  const user = useUser();
  const router = useRouter();
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const verificationCodeInputRef = useRef<HTMLInputElement>(null);
  const [verificationStep, setVerificationStep] = useState<
    "idle" | "code-sent" | "verifying"
  >("idle");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState<string>("");
  const [userPin, setUserPin] = useState("");
  const [userPinError, setUserPinError] = useState("");
  const pinWritePendingRef = useRef(false);
  const savePendingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const userPinStatus = useQuery({
    queryKey: ["user-pin", "configured"],
    queryFn: () => userPinApi<{ configured: boolean }>("configured"),
  });
  const saveUserPin = useMutation({
    mutationFn: (code: string | null) => userPinApi("set", { code }),
    onSuccess: () => {
      setUserPin("");
      setUserPinError("");
      userPinStatus.refetch();
    },
    onError: (error) => setUserPinError(error.message),
  });
  const removeUserPin = () => {
    void runPinWriteOnce(pinWritePendingRef, () =>
      saveUserPin.mutateAsync(null)
    )
      .then(() => toast.success("User PIN removed."))
      .catch(() => {}); // The mutation's onError displays the failure.
  };
  const { countdown, startTimer } = useCountdownTimer(30);

  const {
    control,
    formState: { isDirty },
    getValues,
    reset,
    trigger,
  } = useForm<FormValues>({
    resolver: valibotResolver(FormValues),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phoneNumber: {
        value: user?.phone
          ? parsePhoneNumberWithError(user.phone).formatNational()
          : "",
        isVerified: user.phoneVerified ?? false,
      },
    },
  });

  const mutation = useMutation(
    trpc.auth.verifyPhoneNumber.mutationOptions({
      onSuccess: async (_, vars) => {
        if (vars.type === "send-code") {
          setVerificationStep("code-sent");
          startTimer();
          // Focus on verification code input after state updates and DOM render
          setTimeout(() => {
            verificationCodeInputRef.current?.focus();
          }, 100);
        } else {
          await router.invalidate({
            filter: (d) => d.id === rootRouteId,
            sync: true,
          });
          toast.success("Phone number verified successfully!");
          setVerificationStep("idle");
          setVerificationCode("");
          setVerificationError("");
        }
      },
      onError: (error, vars) => {
        if (vars.type === "send-code") {
          if (error.data?.code === "BAD_REQUEST") {
            toast.error("Invalid phone number.");
          } else {
            console.error("Error sending verification code:", error);
            toast.error("An unknown error occurred. Please try again.");
          }
        } else {
          if (error.data?.code === "BAD_REQUEST") {
            setVerificationError("Invalid verification code.");
          } else {
            console.error("Error verifying code:", error);
            setVerificationError(
              "An unknown error occurred. Please try again."
            );
          }
        }
      },
    })
  );

  const handleSendCode = () => {
    if (!user?.phone) return;
    const normalizedPhone = parsePhoneNumberWithError(user.phone, "CA").format(
      "E.164"
    );
    mutation.mutate({
      type: "send-code",
      phone: normalizedPhone,
    });
  };

  const handleVerifyCode = () => {
    if (!verificationCode) {
      setVerificationError("Please enter the verification code.");
      return;
    }
    if (!user?.phone) return;
    const normalizedPhone = parsePhoneNumberWithError(user.phone, "CA").format(
      "E.164"
    );
    setVerificationError("");
    mutation.mutate({
      type: "verify-code",
      code: verificationCode,
      phone: normalizedPhone,
    });
  };

  const handleResendCode = () => {
    handleSendCode();
  };

  const updateMutation = useMutation({
    mutationKey: ["auth", "updateUser"],
    mutationFn: async (
      data: Partial<
        Omit<FormValues, "phoneNumber"> & {
          phone?: string;
        }
      >
    ) => {
      const response = await authClient.updateUser({
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone === undefined ? undefined : data.phone || null,
      });
      if (response.error) throw new Error(response.error.message);
    },
    onSuccess: async () => {
      await router.invalidate({
        filter: (d) => d.id === rootRouteId,
        sync: true,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const pinIsValid = /^[0-9]{5}$/.test(userPin);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savePendingRef.current) return;
    if (!user) {
      throw new Error("User is not available (shouldn't happen).");
    }
    const profileChanged = isDirty;
    const pinToSave = userPin;
    if (!profileChanged && !pinToSave) return;
    if (pinToSave && !pinIsValid) {
      setUserPinError("Enter a five-digit PIN.");
      return;
    }
    savePendingRef.current = true;
    setIsSaving(true);
    let profileSaved = false;
    try {
      if (profileChanged) {
        if (!(await trigger())) return;
        const data = getValues();
        await updateMutation.mutateAsync({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phoneNumber.value ? undefined : "",
        });
        reset(data);
        profileSaved = true;
      }
      if (pinToSave) {
        await runPinWriteOnce(pinWritePendingRef, () =>
          saveUserPin.mutateAsync(pinToSave)
        );
      }
      toast.success(
        profileChanged
          ? pinToSave
            ? "User profile and PIN updated successfully."
            : "User profile updated successfully."
          : "User PIN updated."
      );
    } catch {
      if (profileSaved && pinToSave) {
        toast.error("Profile changes saved, but PIN update failed.");
      }
      // Each mutation displays its own error.
    } finally {
      savePendingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        User Profile
      </Typography>

      <Collapse in={Boolean(user?.phone && !user?.phoneVerified)}>
        <Box
          sx={{
            p: 3,
            mb: 2,
            border: "2px solid",
            borderColor: "warning.main",
            borderRadius: 2,
            backgroundColor: "rgba(255, 152, 0, 0.15)",
            boxShadow: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              fontWeight: 700,
              color: "warning.dark",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            ⚠️ Phone Verification Required
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, fontWeight: 500, color: "text.primary" }}
          >
            {verificationStep === "idle"
              ? "Your phone number is not verified. Please verify it to enable all features."
              : "A verification code has been sent to your phone. Please enter it below."}
          </Typography>

          {verificationStep === "idle" && (
            <>
              <Button
                data-testid="send-verification-code-button"
                onClick={handleSendCode}
                loading={mutation.isPending}
                fullWidth
              >
                Send Verification Code
              </Button>
            </>
          )}

          {(verificationStep === "code-sent" ||
            verificationStep === "verifying") && (
            <Stack gap={2}>
              <Input
                data-testid="verification-code-input"
                inputRef={verificationCodeInputRef}
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleVerifyCode();
                  }
                }}
                slotProps={{
                  htmlInput: {
                    maxLength: 6,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                }}
                placeholder="Enter 6-digit code"
                disabled={mutation.isPending}
                error={Boolean(verificationError)}
                errorText={verificationError}
              />
              <Stack direction="row" gap={1}>
                <Button
                  data-testid="verify-code-button"
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6}
                  loading={mutation.isPending || updateMutation.isPending}
                  fullWidth
                >
                  Verify Code
                </Button>
                <Button
                  data-testid="resend-code-button"
                  onClick={handleResendCode}
                  disabled={countdown > 0 || mutation.isPending}
                  loading={mutation.isPending}
                  variant="outlined"
                  fullWidth
                >
                  {countdown > 0 ? `Resend (${countdown}s)` : "Resend"}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Collapse>

      <Input
        value={user?.email ?? ""}
        disabled
        label="Email"
        helperText="Contact support to change your email."
      />
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        <Controller
          control={control}
          name="firstName"
          inputProps={{ required: true, label: "First Name" }}
        />
        <Controller
          control={control}
          name="lastName"
          inputProps={{ required: true, label: "Last Name" }}
        />
      </Stack>
      <Controller
        control={control}
        name="phoneNumber"
        render={({ field, fieldState }) => (
          <PhoneInput
            {...field}
            fullWidth
            // disabled={!!user.phone && !user.phoneVerified}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            label="Phone Number"
            helperText="Phone number is used for some dashboard notifications and is optional."
            needsVerification={fieldState.isDirty || !user.phone}
          />
        )}
      />

      {!user.isGeneralAccount && (
        <>
          <Typography variant="h6">User PIN</Typography>
          <Typography>
            {userPinStatus.data?.configured
              ? "A PIN is registered."
              : "No PIN registered."}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1}
            alignItems="center"
          >
            <Input
              label="New five digit PIN"
              value={userPin}
              onChange={(event) => {
                setUserPin(event.target.value);
                setUserPinError("");
              }}
              error={!!userPinError}
              errorText={userPinError}
              fullWidth
              slotProps={{
                htmlInput: {
                  maxLength: 5,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                },
              }}
            />
            {userPinStatus.data?.configured && (
              <Button
                type="button"
                variant="outlined"
                sx={{ flexShrink: 0 }}
                disabled={isSaving || saveUserPin.isPending}
                onClick={removeUserPin}
              >
                Remove PIN
              </Button>
            )}
          </Stack>
        </>
      )}
      <Button
        data-testid="user-profile-submit-button"
        type="submit"
        fullWidth
        disabled={
          isSaving ||
          saveUserPin.isPending ||
          (!isDirty && !pinIsValid) ||
          (!!userPin && !pinIsValid)
        }
        loading={isSaving}
      >
        Save Changes
      </Button>
    </Box>
  );
}
